import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';
import OlcTooltip from '~/components/General/OlcTooltip';
import { MESSAGES } from '~/utils/message';

import './styles.scss';

const contactOptions = [
  {
    icon: <UploadFileOutlinedIcon />,
    title: 'Import from a file',
    description: 'Use an existing contact list and continue through the standard import flow.',
    actions: [
      {
        label: 'Upload a file',
        description: 'Continue to the contacts import page.',
        path: '/contacts/import?origin=contacts',
      },
    ],
  },
  {
    icon: <PersonSearchOutlinedIcon />,
    title: 'Find new contacts',
    description: 'Build a fresh list by choosing the search method that fits your audience.',
    actions: [
      {
        icon: <MyLocationOutlinedIcon />,
        label: 'Radius Around an Address',
        description: 'Enter an address and find contacts within a radius around it.',
        path: '/orders/create-order',
        state: { initialOrderOption: 'find_new', initialFindNewOption: 'addresses' },
      },
      {
        icon: <MapOutlinedIcon />,
        label: 'Search an Area',
        description: 'Pick or draw an area, then filter for the contacts you want.',
        path: '/orders/create-order',
        state: { initialOrderOption: 'find_new', initialFindNewOption: 'locations' },
      },
    ],
  },
];

const AddContactsGuideModal = ({
  disabledMessage = MESSAGES.MODE.LIVE_MODE_ONLY_FEATURE,
  isFindNewContactsDisabled = false,
  onClose = () => {},
  onExited,
  open,
}) => {
  const navigate = useNavigate();
  const [expandedOption, setExpandedOption] = useState(null);
  const activeOption = contactOptions.find((option) => option.title === expandedOption);

  const closeModal = () => {
    setExpandedOption(null);
    onClose();
  };

  const handleExited = () => {
    setExpandedOption(null);
    onExited?.();
  };

  const continueTo = (action) => {
    closeModal();
    navigate(action.path, action.state ? { state: action.state } : undefined);
  };

  return (
    <OlcFormModal
      open={open}
      onClose={closeModal}
      title="Add your contacts"
      subtitle="Choose whether to use a list you already have or build a new targeted audience."
      className="add-contacts-guide-modal getting-started-guide-modal is-card-choice-modal"
      dialogProps={{
        onExited: handleExited,
        transitionDuration: { enter: 160, exit: 100 },
      }}
      footer={
        <>
          <span className="getting-started-guide-note">
            You can add more contacts or build another audience anytime.
          </span>
          <OlcButton type="button" variant="secondary" onClick={closeModal}>
            Cancel
          </OlcButton>
        </>
      }
    >
      <div className="getting-started-guide-options">
        {contactOptions.map((option) => {
          const isExpandable = option.actions.length > 1;
          const cardAction = option.actions.length === 1 ? option.actions[0] : null;
          const isFindNewContactsOption = option.title === 'Find new contacts';
          const isOptionDisabled = isFindNewContactsOption && isFindNewContactsDisabled;

          return (
            <OlcTooltip
              arrow
              key={option.title}
              disableInteractive={!isOptionDisabled}
              placement="top"
              title={isOptionDisabled ? disabledMessage : ''}
            >
              <span className="getting-started-guide-optionTooltipTarget">
                <button
                  type="button"
                  className={`getting-started-guide-option is-clickable ${
                    expandedOption === option.title && !isOptionDisabled ? 'is-expanded' : ''
                  } ${isOptionDisabled ? 'is-disabled' : ''}`.trim()}
                  aria-disabled={isOptionDisabled}
                  aria-label={
                    isExpandable
                      ? `${option.title}: show options`
                      : `${option.title}: ${cardAction.label}`
                  }
                  aria-expanded={isExpandable ? expandedOption === option.title : undefined}
                  disabled={isOptionDisabled}
                  onClick={() => {
                    if (isOptionDisabled) return;

                    if (isExpandable) {
                      setExpandedOption(option.title);
                      return;
                    }

                    continueTo(cardAction);
                  }}
                >
                  <span className="getting-started-guide-option-icon">{option.icon}</span>
                  <span className="getting-started-guide-option-title">{option.title}</span>
                  <span className="getting-started-guide-option-description">
                    {isOptionDisabled ? disabledMessage : option.description}
                  </span>
                </button>
              </span>
            </OlcTooltip>
          );
        })}
      </div>
      {activeOption ? (
        <div
          className="getting-started-guide-suboptions"
          aria-label={`${activeOption.title} options`}
        >
          <h3 className="getting-started-guide-suboptions-title">
            How do you want to find contacts?
          </h3>
          {activeOption.actions.map((action) => (
            <button
              type="button"
              className="getting-started-guide-option is-clickable"
              key={action.label}
              onClick={() => continueTo(action)}
            >
              <span className="getting-started-guide-option-icon">{action.icon}</span>
              <span className="getting-started-guide-option-title">{action.label}</span>
              <span className="getting-started-guide-option-description">
                {action.description}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </OlcFormModal>
  );
};

export default AddContactsGuideModal;
